import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const VIEWPORT = 320; // px - square viewport with a circular cutout
const OUTPUT_SIZE = 400; // px - exported image size

/**
 * HeadshotCropper
 * Lets the user drag and zoom an uploaded image inside a circular viewport.
 * Whatever appears inside the circle on Save becomes the saved profile picture.
 *
 * Props:
 *  - file: File (the picked image)
 *  - open: bool
 *  - onClose(): close dialog without saving
 *  - onSave(blob): receives the cropped image as a PNG Blob
 */
export default function HeadshotCropper({ file, open, onClose, onSave }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1); // 1 = fit shortest side to viewport
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // pan offset in pixels
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const imgRef = useRef(null);
  const [saving, setSaving] = useState(false);

  // Read picked file once
  useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => setImgSrc(e.target.result);
    reader.readAsDataURL(file);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [file]);

  const onImgLoad = (e) => {
    setNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });
  };

  // Compute base scale: shortest side of image fits VIEWPORT
  const baseScale = naturalSize.w && naturalSize.h
    ? VIEWPORT / Math.min(naturalSize.w, naturalSize.h)
    : 1;
  const totalScale = baseScale * zoom;
  const drawnW = naturalSize.w * totalScale;
  const drawnH = naturalSize.h * totalScale;

  // Clamp pan so the image always fully covers the circle
  const clampOffset = useCallback((x, y) => {
    const maxX = Math.max(0, (drawnW - VIEWPORT) / 2);
    const maxY = Math.max(0, (drawnH - VIEWPORT) / 2);
    return { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
  }, [drawnW, drawnH]);

  // Re-clamp when zoom changes
  useEffect(() => {
    setOffset(o => clampOffset(o.x, o.y));
  }, [zoom, clampOffset]);

  const startDrag = (clientX, clientY) => {
    setDragging(true);
    dragStart.current = { x: clientX, y: clientY, ox: offset.x, oy: offset.y };
  };
  const moveDrag = (clientX, clientY) => {
    if (!dragging) return;
    const nx = dragStart.current.ox + (clientX - dragStart.current.x);
    const ny = dragStart.current.oy + (clientY - dragStart.current.y);
    setOffset(clampOffset(nx, ny));
  };
  const endDrag = () => setDragging(false);

  const handleSave = async () => {
    if (!imgRef.current || !naturalSize.w) return;
    setSaving(true);
    try {
      // Render to canvas at OUTPUT_SIZE
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');

      // Map viewport-space transform to output-space
      const k = OUTPUT_SIZE / VIEWPORT;
      const drawW = drawnW * k;
      const drawH = drawnH * k;
      const dx = (OUTPUT_SIZE - drawW) / 2 + offset.x * k;
      const dy = (OUTPUT_SIZE - drawH) / 2 + offset.y * k;

      // Optional: white background under transparent PNGs
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

      ctx.drawImage(imgRef.current, dx, dy, drawW, drawH);

      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
      if (!blob) throw new Error('Could not encode image');
      await onSave(blob);
    } catch {
      // parent toasts
    } finally {
      setSaving(false);
    }
  };

  const reset = () => { setZoom(1); setOffset({ x: 0, y: 0 }); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="headshot-cropper">
        <DialogHeader>
          <DialogTitle>Position your photo</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2 mb-2">
          Drag to reposition. Zoom in or out so your face fills the circle. What's inside the circle becomes your profile picture.
        </p>

        <div className="flex justify-center mb-4">
          <div
            className="relative bg-slate-900 rounded-lg overflow-hidden touch-none select-none"
            style={{ width: VIEWPORT, height: VIEWPORT, cursor: dragging ? 'grabbing' : 'grab' }}
            onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX, e.clientY); }}
            onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchStart={(e) => { const t = e.touches[0]; startDrag(t.clientX, t.clientY); }}
            onTouchMove={(e) => { const t = e.touches[0]; moveDrag(t.clientX, t.clientY); }}
            onTouchEnd={endDrag}
            data-testid="cropper-viewport"
          >
            {imgSrc && (
              <img
                ref={imgRef}
                src={imgSrc}
                alt="To crop"
                onLoad={onImgLoad}
                draggable={false}
                style={{
                  position: 'absolute',
                  width: `${drawnW}px`,
                  height: `${drawnH}px`,
                  maxWidth: 'none',
                  maxHeight: 'none',
                  left: `${(VIEWPORT - drawnW) / 2 + offset.x}px`,
                  top: `${(VIEWPORT - drawnH) / 2 + offset.y}px`,
                  pointerEvents: 'none',
                  userSelect: 'none',
                  objectFit: 'fill',
                }}
              />
            )}
            {/* Circular mask overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(circle at center, transparent ${VIEWPORT / 2 - 1}px, rgba(0,0,0,0.55) ${VIEWPORT / 2}px)`,
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none border-2 border-white/80 rounded-full"
              style={{ margin: 'auto', width: VIEWPORT, height: VIEWPORT, boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Zoom slider */}
        <div className="flex items-center gap-3 px-1">
          <ZoomOut className="h-4 w-4 text-slate-400" />
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.01}
            onValueChange={(v) => setZoom(v[0])}
            className="flex-1"
            data-testid="cropper-zoom"
          />
          <ZoomIn className="h-4 w-4 text-slate-400" />
        </div>

        <div className="flex items-center justify-between gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={reset} data-testid="cropper-reset">
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="cropper-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !imgSrc}
              className="bg-gradient-to-r from-brand-navy to-brand-sage text-white"
              data-testid="cropper-save"
            >
              {saving ? 'Saving...' : 'Save photo'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
